import styled from "styled-components";
import { Link } from "react-router-dom";

/**
 * Clicking the photo opens the product page.
 * The small switch in the corner still toggles that single lamp in place —
 * it sits as a sibling of the link, not inside it, so the two never fight.
 */
export default function ProductCard({ product, lit, onToggle }) {
  const href = `/produkti/${product.id}`;

  return (
    <Card data-card>
      <FrameWrap $lit={lit}>
        <Frame to={href} aria-label={`Shiko ${product.name}`}>
          <Shot src={product.off} alt={product.name} $show={!lit} />
          <Shot src={product.on} alt="" aria-hidden="true" $show={lit} />
          <Open data-hover-only>shiko →</Open>
        </Frame>

        <Toggle
          type="button"
          onClick={onToggle}
          aria-pressed={lit}
          aria-label={`${product.name} — ${lit ? "fik" : "ndez"}`}
          $lit={lit}
        >
          <Bulb $lit={lit} />
        </Toggle>
      </FrameWrap>

      <Meta to={href}>
        <Row>
          <Name>{product.name}</Name>
          <Price>€{product.price}</Price>
        </Row>
        <Row>
          <Type>{product.type}</Type>
          <Spec>{product.spec}</Spec>
        </Row>
      </Meta>
    </Card>
  );
}

const Card = styled.div`
  width: 100%;
`;

const FrameWrap = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 5;
  border-radius: 2px;
  overflow: hidden;
  background: var(--bg-soft);
  box-shadow: ${({ $lit }) => ($lit ? "var(--glow)" : "none")};
  transition:
    box-shadow var(--t-theme) var(--ease),
    background var(--t-theme) var(--ease);
`;

const Frame = styled(Link)`
  position: absolute;
  inset: 0;
  display: block;
  -webkit-tap-highlight-color: transparent;

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: -3px;
  }
`;

const Shot = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: ${({ $show }) => ($show ? 1 : 0)};
  transition:
    opacity 750ms var(--ease),
    transform 900ms var(--ease);
  will-change: opacity;

  ${Frame}:hover & {
    transform: scale(1.018);
  }
`;

const Open = styled.span`
  position: absolute;
  left: 14px;
  bottom: 14px;
  font-size: 9px;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  padding: 6px 11px;
  border-radius: 999px;
  color: #1a1815;
  background: rgba(255, 255, 255, 0.78);
  backdrop-filter: blur(6px);
  opacity: 0;
  transform: translateY(5px);
  transition:
    opacity 300ms var(--ease),
    transform 300ms var(--ease);

  ${Frame}:hover & {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Toggle = styled.button`
  position: absolute;
  right: 12px;
  bottom: 12px;
  z-index: 3;
  width: 44px;
  height: 44px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  backdrop-filter: blur(8px);
  background: ${({ $lit }) =>
    $lit ? "rgba(20,18,16,0.5)" : "rgba(255,255,255,0.7)"};
  box-shadow: ${({ $lit }) =>
    $lit ? "0 0 22px rgba(232,168,106,0.5)" : "0 1px 3px rgba(0,0,0,0.14)"};
  transition:
    background 500ms var(--ease),
    box-shadow 500ms var(--ease);

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 3px;
  }
`;

const Bulb = styled.span`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${({ $lit }) => ($lit ? "#f6d9ac" : "rgba(26,24,21,0.32)")};
  box-shadow: ${({ $lit }) =>
    $lit ? "0 0 14px 3px rgba(246,217,172,0.85)" : "none"};
  transition:
    background 500ms var(--ease),
    box-shadow 500ms var(--ease);
`;

const Meta = styled(Link)`
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 14px 2px 0;
  color: inherit;
  text-decoration: none;

  &:hover span:first-child {
    color: var(--accent);
  }
`;

const Row = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
`;

const Name = styled.span`
  font-family: "Instrument Serif", Georgia, serif;
  font-size: 20px;
  letter-spacing: -0.01em;
  transition: color 260ms var(--ease);

  @media (max-width: 560px) {
    font-size: 17px;
  }
`;

const Price = styled.span`
  font-size: 13px;
  color: var(--ink-soft);
`;

const Type = styled.span`
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ink-soft);
`;

const Spec = styled.span`
  font-size: 11px;
  color: var(--ink-soft);
  white-space: nowrap;

  @media (max-width: 560px) {
    display: none;
  }
`;
